// src/app/modules/Settings/DeveloperTab.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import {
  Key, Webhook, Plus, Trash2, RefreshCw, Copy, Eye, EyeOff,
  CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp,
  X, Clock, RotateCcw, Activity, Shield, Zap, Check,
} from "lucide-react";
import { toast } from "react-toastify";
import * as devCrud from "../../Dashbord/_redux/dashboard/Crud.developer";
import { useFeatureGate } from "../../Dashbord/components/FeatureGate";
import "./developer.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-NG", { year: "numeric", month: "short", day: "numeric" }) : "—";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("en-NG", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

const copyToClipboard = (text, label = "Copied") => {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied to clipboard`));
};

const STATUS_DOT = ({ active }) => (
  <span className={`dev-status-dot ${active ? "dev-status-dot--active" : "dev-status-dot--inactive"}`} />
);

// ── Scope Badge ───────────────────────────────────────────────────────────────

const ScopeBadge = ({ scope }) => (
  <span className="dev-scope-badge">{scope}</span>
);

// ── Masked Secret ─────────────────────────────────────────────────────────────

function MaskedSecret({ value, label = "Key" }) {
  const [visible, setVisible] = useState(false);
  if (!value) return <span className="dev-mono text-muted">—</span>;

  const display = visible ? value : `${value.slice(0, 8)}${"•".repeat(24)}`;

  return (
    <span className="dev-secret-row">
      <span className="dev-mono">{display}</span>
      <button className="dev-icon-btn" onClick={() => setVisible(v => !v)} title={visible ? "Hide" : "Show"}>
        {visible ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
      <button className="dev-icon-btn" onClick={() => copyToClipboard(value, label)} title="Copy">
        <Copy size={13} />
      </button>
    </span>
  );
}

// ── Modal Shell ───────────────────────────────────────────────────────────────

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div className="dev-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dev-modal">
        <div className="dev-modal__header">
          <div>
            <div className="dev-modal__title">{title}</div>
            {subtitle && <div className="dev-modal__subtitle">{subtitle}</div>}
          </div>
          <button className="dev-icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="dev-modal__body">{children}</div>
        {footer && <div className="dev-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

// ── Create API Key Modal ──────────────────────────────────────────────────────

function CreateKeyModal({ orgId, availableScopes, onCreated, onClose }) {
  const [name, setName]           = useState("");
  const [scopes, setScopes]       = useState([]);
  const [expiry, setExpiry]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [newKey, setNewKey]       = useState(null); // show once after creation

  const toggleScope = (s) =>
    setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("Key name is required");
    if (scopes.length === 0) return toast.error("Select at least one scope");

    setLoading(true);
    try {
      const payload = { organization_id: orgId, name: name.trim(), scopes };
      if (expiry) payload.expires_in_days = Number(expiry);
      const res = await devCrud.createApiKey(payload);
      const created = res.data;
      setNewKey(created.raw_key || created.key || created.api_key);
      onCreated(created);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  if (newKey) {
    return (
      <ModalShell
        title="API Key Created"
        subtitle="Copy your key now — it won't be shown again."
        onClose={onClose}
        footer={<button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>}
      >
        <div className="dev-alert dev-alert--warning">
          <AlertTriangle size={14} />
          <span>Store this key securely. For security reasons, it <strong>cannot be retrieved</strong> after closing this dialog.</span>
        </div>
        <div className="dev-key-reveal">
          <span className="dev-mono dev-key-reveal__value">{newKey}</span>
          <button className="btn btn-sm btn-primary" onClick={() => copyToClipboard(newKey, "API Key")}>
            <Copy size={13} className="mr-1" /> Copy Key
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title="Create API Key"
      subtitle="API keys allow external services to authenticate with Sety's API."
      onClose={onClose}
      footer={
        <>
          <button className="sety-btn sety-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={loading}>
            {loading ? <><RefreshCw size={13} className="spin mr-1" />Creating...</> : "Create Key"}
          </button>
        </>
      }
    >
      <div className="dev-form-group">
        <label className="dev-label">Key Name</label>
        <input
          className="form-control form-control-sm"
          placeholder="e.g. Production Server, CI Pipeline"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <span className="dev-hint">A descriptive name to identify this key.</span>
      </div>

      <div className="dev-form-group">
        <label className="dev-label">Scopes</label>
        <div className="dev-scope-grid">
          {availableScopes.map(s => (
            <label key={s} className={`dev-scope-check ${scopes.includes(s) ? "dev-scope-check--active" : ""}`}>
              <input
                type="checkbox"
                checked={scopes.includes(s)}
                onChange={() => toggleScope(s)}
              />
              <Check size={10} className="dev-scope-check__icon" />
              <span>{s}</span>
            </label>
          ))}
        </div>
        <span className="dev-hint">Restrict what this key can access.</span>
      </div>

      <div className="dev-form-group">
        <label className="dev-label">Expiry <span className="dev-optional">(optional)</span></label>
        <select className="form-control form-control-sm" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
          <option value="">Never expires</option>
          <option value="30">30 days</option>
          <option value="90">90 days</option>
          <option value="180">180 days</option>
          <option value="365">1 year</option>
        </select>
      </div>
    </ModalShell>
  );
}

// ── Create Webhook Modal ──────────────────────────────────────────────────────

function CreateWebhookModal({ orgId, availableEvents, onCreated, onClose, existing }) {
  const [url, setUrl]             = useState(existing?.url || "");
  const [events, setEvents]       = useState(existing?.events || []);
  const [description, setDesc]    = useState(existing?.description || "");
  const [loading, setLoading]     = useState(false);
  const isEdit = !!existing;

  const toggleEvent = (e) =>
    setEvents(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const handleSubmit = async () => {
    if (!url.trim() || !url.startsWith("https://"))
      return toast.error("Please enter a valid HTTPS URL");
    if (events.length === 0)
      return toast.error("Select at least one event to subscribe to");

    setLoading(true);
    try {
      let res;
      const payload = { organization_id: orgId, url: url.trim(), events, description };
      if (isEdit) {
        res = await devCrud.updateWebhook(existing.endpoint_id || existing._id, payload);
        toast.success("Webhook updated");
      } else {
        res = await devCrud.createWebhook(payload);
        toast.success("Webhook created");
      }
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save webhook");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Edit Webhook" : "Create Webhook"}
      subtitle="Sety will POST a signed JSON payload to your URL when events occur."
      onClose={onClose}
      footer={
        <>
          <button className="sety-btn sety-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading}>
            {loading
              ? <><RefreshCw size={13} className="spin mr-1" />{isEdit ? "Saving..." : "Creating..."}</>
              : isEdit ? "Save Changes" : "Create Webhook"}
          </button>
        </>
      }
    >
      <div className="dev-form-group">
        <label className="dev-label">Endpoint URL</label>
        <input
          className="form-control form-control-sm dev-mono"
          placeholder="https://yourserver.com/webhooks/sety"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
        />
        <span className="dev-hint">Must be HTTPS. Sety will send a POST request to this URL.</span>
      </div>

      <div className="dev-form-group">
        <label className="dev-label">Description <span className="dev-optional">(optional)</span></label>
        <input
          className="form-control form-control-sm"
          placeholder="e.g. Notify our alerting pipeline on threat events"
          value={description}
          onChange={(e) => setDesc(e.target.value)}
        />
      </div>

      <div className="dev-form-group">
        <label className="dev-label">Events</label>
        <div className="dev-event-list">
          {availableEvents.map(ev => (
            <label key={ev} className={`dev-event-check ${events.includes(ev) ? "dev-event-check--active" : ""}`}>
              <input type="checkbox" checked={events.includes(ev)} onChange={() => toggleEvent(ev)} />
              <Check size={10} className="dev-event-check__icon" />
              <span className="dev-mono">{ev}</span>
            </label>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

// ── Deliveries Drawer ─────────────────────────────────────────────────────────

function DeliveriesDrawer({ endpointId, orgId, onClose }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [retrying, setRetrying]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await devCrud.listDeliveries(endpointId, { organization_id: orgId, limit: 20 });
      setDeliveries(res.data.results || res.data.deliveries || res.data || []);
    } catch {
      toast.error("Failed to load delivery logs");
    } finally {
      setLoading(false);
    }
  }, [endpointId, orgId]);

  useEffect(() => { load(); }, [load]);

  const handleRetry = async (deliveryId) => {
    setRetrying(deliveryId);
    try {
      await devCrud.retryDelivery(deliveryId, orgId);
      toast.success("Retry queued");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  return (
    <ModalShell
      title="Delivery Logs"
      subtitle="Recent webhook dispatch attempts for this endpoint."
      onClose={onClose}
      footer={
        <button className="sety-btn sety-btn-ghost" onClick={onClose}>Close</button>
      }
    >
      {loading ? (
        <div className="dev-loading"><RefreshCw size={18} className="spin" /></div>
      ) : deliveries.length === 0 ? (
        <div className="dev-empty">
          <Activity size={26} />
          <p>No deliveries yet</p>
        </div>
      ) : (
        <div className="dev-delivery-list">
          {deliveries.map((d, i) => {
            const ok = d.status === "success" || d.response_status_code < 300;
            const id = d.delivery_id || d._id;
            return (
              <div key={id || i} className={`dev-delivery-row ${ok ? "" : "dev-delivery-row--failed"}`}>
                <span className={`dev-delivery-status ${ok ? "success" : "failed"}`}>
                  {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  {ok ? d.response_status_code || "200" : d.response_status_code || "ERR"}
                </span>
                <span className="dev-mono dev-delivery-event">{d.event_type || d.event || "—"}</span>
                <span className="dev-delivery-time">{fmtDateTime(d.created_at || d.attempted_at)}</span>
                {!ok && (
                  <button
                    className="dev-retry-btn"
                    disabled={retrying === id}
                    onClick={() => handleRetry(id)}
                  >
                    {retrying === id
                      ? <RefreshCw size={11} className="spin" />
                      : <><RotateCcw size={11} /> Retry</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ModalShell>
  );
}

// ── Rotate Secret Confirm ─────────────────────────────────────────────────────

function RotateSecretModal({ endpointId, orgId, onRotated, onClose }) {
  const [loading, setLoading]   = useState(false);
  const [newSecret, setSecret]  = useState(null);

  const handleRotate = async () => {
    setLoading(true);
    try {
      const res = await devCrud.rotateWebhookSecret(endpointId, orgId);
      const secret = res.data.signing_secret || res.data.secret;
      setSecret(secret);
      if (onRotated) onRotated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to rotate secret");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (newSecret) {
    return (
      <ModalShell title="New Signing Secret" onClose={onClose}
        footer={<button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>}
      >
        <div className="dev-alert dev-alert--warning">
          <AlertTriangle size={14} />
          <span>Update your server immediately — the old secret is now invalid.</span>
        </div>
        <div className="dev-key-reveal">
          <span className="dev-mono dev-key-reveal__value">{newSecret}</span>
          <button className="btn btn-sm btn-primary" onClick={() => copyToClipboard(newSecret, "Signing secret")}>
            <Copy size={13} className="mr-1" /> Copy
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Rotate Signing Secret?" onClose={onClose}
      footer={
        <>
          <button className="sety-btn sety-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={handleRotate} disabled={loading}>
            {loading ? <><RefreshCw size={13} className="spin mr-1" />Rotating...</> : "Rotate Secret"}
          </button>
        </>
      }
    >
      <p className="dev-confirm-text">
        A new signing secret will be generated. Your existing secret will stop working immediately.
        Make sure to update your webhook handler before confirming.
      </p>
    </ModalShell>
  );
}

// ── API Keys Panel ────────────────────────────────────────────────────────────

function ApiKeysPanel({ orgId, scopes }) {
  const [keys, setKeys]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setCreate]   = useState(false);
  const [revoking, setRevoking]   = useState(null);

  const load = async () => {
    try {
      const res = await devCrud.listApiKeys(orgId);
      setKeys(res.data.keys || res.data.results || res.data || []);
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (orgId) load(); }, [orgId]);

  const handleRevoke = async (keyId) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    setRevoking(keyId);
    try {
      await devCrud.revokeApiKey(keyId, orgId);
      toast.success("API key revoked");
      setKeys(prev => prev.filter(k => (k.key_id || k._id) !== keyId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to revoke key");
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="dev-section">
      <div className="dev-section__head">
        <div className="dev-section__title-row">
          <Key size={14} />
          <h6>API Keys</h6>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setCreate(true)}>
          <Plus size={13} className="mr-1" /> New Key
        </button>
      </div>

      <div className="dev-section__body--flush">
        {loading ? (
          <div className="dev-loading"><RefreshCw size={18} className="spin" /></div>
        ) : keys.length === 0 ? (
          <div className="dev-empty">
            <Key size={26} />
            <p>No API keys yet</p>
            <p className="dev-empty__hint">Create a key to authenticate external API requests</p>
          </div>
        ) : (
          <div className="dev-key-list">
            {keys.map(k => {
              const id = k.key_id || k._id;
              const isActive = k.is_active !== false && k.status !== "revoked";
              return (
                <div key={id} className="dev-key-row">
                  <div className="dev-key-row__main">
                    <STATUS_DOT active={isActive} />
                    <div className="dev-key-row__info">
                      <span className="dev-key-row__name">{k.name}</span>
                      <MaskedSecret value={k.key_prefix ? `${k.key_prefix}...` : k.masked_key} label="API Key" />
                    </div>
                  </div>
                  <div className="dev-key-row__meta">
                    <div className="dev-key-row__scopes">
                      {(k.scopes || []).slice(0, 4).map(s => <ScopeBadge key={s} scope={s} />)}
                      {(k.scopes || []).length > 4 && (
                        <span className="dev-scope-badge dev-scope-badge--more">+{k.scopes.length - 4}</span>
                      )}
                    </div>
                    <div className="dev-key-row__dates">
                      <span>Created {fmtDate(k.created_at)}</span>
                      {k.expires_at && <span>· Expires {fmtDate(k.expires_at)}</span>}
                      {k.last_used_at && <span>· Last used {fmtDate(k.last_used_at)}</span>}
                    </div>
                  </div>
                  <div className="dev-key-row__actions">
                    {isActive && (
                      <button
                        className="dev-icon-btn dev-icon-btn--danger"
                        title="Revoke"
                        disabled={revoking === id}
                        onClick={() => handleRevoke(id)}
                      >
                        {revoking === id ? <RefreshCw size={13} className="spin" /> : <Trash2 size={13} />}
                      </button>
                    )}
                    {!isActive && (
                      <span className="dev-revoked-badge">Revoked</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateKeyModal
          orgId={orgId}
          availableScopes={scopes}
          onCreated={(newKey) => setKeys(prev => [newKey, ...prev])}
          onClose={() => setCreate(false)}
        />
      )}
    </div>
  );
}

// ── Webhooks Panel ────────────────────────────────────────────────────────────

function WebhooksPanel({ orgId, eventTypes }) {
  const [webhooks, setWebhooks]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setCreate]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [deliveries, setDeliveries] = useState(null); // endpoint_id
  const [rotating, setRotating]     = useState(null);

  const load = async () => {
    try {
      const res = await devCrud.listWebhooks(orgId);
      setWebhooks(res.data.endpoints || res.data.webhooks || res.data.results || res.data || []);
    } catch {
      toast.error("Failed to load webhooks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (orgId) load(); }, [orgId]);

  const handleDelete = async (endpointId) => {
    if (!window.confirm("Delete this webhook endpoint?")) return;
    setDeleting(endpointId);
    try {
      await devCrud.deleteWebhook(endpointId, orgId);
      toast.success("Webhook deleted");
      setWebhooks(prev => prev.filter(w => (w.endpoint_id || w._id) !== endpointId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete webhook");
    } finally {
      setDeleting(null);
    }
  };

  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="dev-section">
      <div className="dev-section__head">
        <div className="dev-section__title-row">
          <Webhook size={14} />
          <h6>Webhooks</h6>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setCreate(true)}>
          <Plus size={13} className="mr-1" /> New Endpoint
        </button>
      </div>

      <div className="dev-section__body--flush">
        {loading ? (
          <div className="dev-loading"><RefreshCw size={18} className="spin" /></div>
        ) : webhooks.length === 0 ? (
          <div className="dev-empty">
            <Webhook size={26} />
            <p>No webhook endpoints yet</p>
            <p className="dev-empty__hint">Receive real-time event notifications on your server</p>
          </div>
        ) : (
          <div className="dev-webhook-list">
            {webhooks.map(w => {
              const id      = w.endpoint_id || w._id;
              const isActive = w.is_active !== false && w.status !== "disabled";
              const open    = expanded[id];

              return (
                <div key={id} className={`dev-webhook-row ${open ? "dev-webhook-row--open" : ""}`}>
                  {/* Header row */}
                  <div className="dev-webhook-row__head" onClick={() => toggleExpand(id)}>
                    <div className="dev-webhook-row__left">
                      <STATUS_DOT active={isActive} />
                      <div>
                        <div className="dev-mono dev-webhook-url">{w.url}</div>
                        {w.description && <div className="dev-webhook-desc">{w.description}</div>}
                      </div>
                    </div>
                    <div className="dev-webhook-row__right">
                      <div className="dev-webhook-stats">
                        {w.success_rate !== undefined && (
                          <span className="dev-stat-pill">
                            <CheckCircle size={10} /> {Math.round(w.success_rate)}%
                          </span>
                        )}
                        <span className="dev-date-text">{fmtDate(w.created_at)}</span>
                      </div>
                      <div className="dev-webhook-row__actions" onClick={e => e.stopPropagation()}>
                        <button className="dev-icon-btn" title="Delivery logs" onClick={() => setDeliveries(id)}>
                          <Activity size={13} />
                        </button>
                        <button className="dev-icon-btn" title="Edit" onClick={() => setEditing(w)}>
                          <Shield size={13} />
                        </button>
                        <button className="dev-icon-btn" title="Rotate secret" onClick={() => setRotating(id)}>
                          <RotateCcw size={13} />
                        </button>
                        <button
                          className="dev-icon-btn dev-icon-btn--danger"
                          title="Delete"
                          disabled={deleting === id}
                          onClick={() => handleDelete(id)}
                        >
                          {deleting === id ? <RefreshCw size={13} className="spin" /> : <Trash2 size={13} />}
                        </button>
                        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded events */}
                  {open && (
                    <div className="dev-webhook-row__events">
                      <span className="dev-events-label">Subscribed events</span>
                      <div className="dev-events-wrap">
                        {(w.events || []).map(ev => (
                          <span key={ev} className="dev-event-pill">{ev}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateWebhookModal
          orgId={orgId}
          availableEvents={eventTypes}
          onCreated={(wh) => setWebhooks(prev => [wh, ...prev])}
          onClose={() => setCreate(false)}
        />
      )}

      {editing && (
        <CreateWebhookModal
          orgId={orgId}
          availableEvents={eventTypes}
          existing={editing}
          onCreated={(updated) => {
            setWebhooks(prev =>
              prev.map(w => (w.endpoint_id || w._id) === (updated.endpoint_id || updated._id) ? updated : w)
            );
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {deliveries && (
        <DeliveriesDrawer
          endpointId={deliveries}
          orgId={orgId}
          onClose={() => setDeliveries(null)}
        />
      )}

      {rotating && (
        <RotateSecretModal
          endpointId={rotating}
          orgId={orgId}
          onRotated={() => load()}
          onClose={() => setRotating(null)}
        />
      )}
    </div>
  );
}

// ── Locked state banner ───────────────────────────────────────────────────────

function DevApiLockedBanner() {
  const { guardAction } = useFeatureGate();
  return (
    <div className="dev-locked-banner">
      <div className="dev-locked-banner__icon">
        <Key size={32} />
      </div>
      <h3 className="dev-locked-banner__title">Developer API Access</h3>
      <p className="dev-locked-banner__desc">
        Build custom integrations using API keys and webhook endpoints.
        Available on higher plans.
      </p>
      <button
        className="dev-locked-banner__btn"
        onClick={() => guardAction(() => {}, 'api_access')}
      >
        View Plans
      </button>
    </div>
  );
}

// ── DeveloperTab (main export) ────────────────────────────────────────────────

export function DeveloperTab() {
  const { org } = useSelector(
    (state) => ({ org: state.auth.organization }),
    shallowEqual
  );
  const orgId = org?.organization_id;
  const { canUseFeature } = useFeatureGate();

  const [scopes, setScopes]           = useState([]);
  const [eventTypes, setEventTypes]   = useState([]);
  const [metaLoaded, setMetaLoaded]   = useState(false);

  const hasApiAccess = canUseFeature('api_access');

  useEffect(() => {
    if (!hasApiAccess) return;
    Promise.all([
      devCrud.listScopes().then(r => setScopes(r.data.scopes || r.data || [])),
      devCrud.listEventTypes().then(r => setEventTypes(r.data.events || r.data || [])),
    ]).finally(() => setMetaLoaded(true));
  }, [hasApiAccess]);

  if (!hasApiAccess) {
    return (
      <div className="developer-tab">
        <DevApiLockedBanner />
      </div>
    );
  }

  if (!metaLoaded) {
    return <div className="dev-loading"><RefreshCw size={20} className="spin" /></div>;
  }

  return (
    <div className="developer-tab">
      {/* Header banner */}
      <div className="dev-banner">
        <div className="dev-banner__icon"><Zap size={18} /></div>
        <div>
          <div className="dev-banner__title">Developer Settings</div>
          <div className="dev-banner__sub">
            Manage API keys and webhook endpoints for integrating Sety into your systems.
            Read the <a href="https://docs.sety.io" target="_blank" rel="noreferrer">API docs</a> to get started.
          </div>
        </div>
      </div>

      <ApiKeysPanel orgId={orgId} scopes={scopes} />
      <WebhooksPanel orgId={orgId} eventTypes={eventTypes} />
    </div>
  );
}